// components/ProductDetailView.jsx
import { useEffect, useState, useMemo, useCallback } from "react";
import { placeholderImage, toPublicStorageUrl } from "../config/imageConfig";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import SecurityIcon from "@mui/icons-material/Security";
import RefreshIcon from "@mui/icons-material/Refresh";
import ShareIcon from "@mui/icons-material/Share";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import WishlistToggle from "./WishlistToggle";
import TryOnStage from "./TryOnStage";
import morphLayout from "../utils/morphLayout";
import { registerInlineTryOn } from "../utils/inlineTryOn";
import { useCart } from "../context/CartContext";
import { getColorHex } from "../utils/colorHex";


// Utility: clean image URL
const cleanImageUrl = (url) => {
  if (typeof url !== "string") return "";
  return toPublicStorageUrl(url.trim());
};

// Utility: safe seed for Picsum (NO SPACES!)
const safeSeed = (input) => {
  return encodeURIComponent(String(input).replace(/\s+/g, "-").substring(0, 50));
};

// ✅ MAIN COMPONENT — now takes product data as prop
export default function ProductDetailView({ 
  productDetail, 
  customerId,
  onBack 
}) {
  const { cartItems, addToCart, updateQuantity } = useCart();

  const [selectedSize, setSelectedSize] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [previewQuantity, setPreviewQuantity] = useState(1);

  const [tryOnOpen, setTryOnOpen] = useState(false);
  // Inline try-on: `autoStart` is the agent's mode; `tryOnSession` remounts
  // the mirror so a second agent request starts a fresh render.
  const [tryOnAutoStart, setTryOnAutoStart] = useState(null);
  const [tryOnSession, setTryOnSession] = useState(0);
  // Opening and closing re-lay the page (photo and mirror side by side,
  // details below), so both run as an animated morph.
  const openTryOn = useCallback((autoStart = null) => {
    morphLayout(() => {
      setTryOnAutoStart(autoStart);
      setTryOnSession((n) => n + 1);
      setTryOnOpen(true);
    });
  }, []);
  const closeTryOn = useCallback(() => {
    morphLayout(() => setTryOnOpen(false));
  }, []);

  // Extract product safely
  const product = productDetail?.product;
  // Let an agent-started try-on for this product open here, inline, instead
  // of in the fallback popup.
  useEffect(
    () =>
      registerInlineTryOn(product?.id, {
        open: ({ autoStart }) => openTryOn(autoStart),
        close: closeTryOn,
      }),
    [product?.id, openTryOn, closeTryOn]
  );

  // Collect all images
  const allImages = useMemo(() => {
    const images = [];
    if (product?.images) {
      product.images.forEach((img, i) =>
        images.push({ url: img, label: `Main ${i + 1}` })
      );
    }
    if (productDetail?.variants) {
      productDetail.variants.forEach((variant) => {
        if (variant.images) {
          variant.images.forEach((img) =>
            images.push({ url: img, label: variant.attributes?.color || "Variant" })
          );
        }
      });
    }
    return images.length > 0 ? images : [{ url: "", label: "No Image" }];
  }, [productDetail]);

  // Colors
  const colorList = useMemo(() => {
    const colors = [];
    if (productDetail?.variants) {
      productDetail.variants.forEach((v) => {
        if (v.attributes?.color) colors.push(v.attributes.color);
      });
    }
    if (colors.length === 0 && product?.attributes?.color) {
      const productColors = String(product.attributes.color).split(",").map(c => c.trim()).filter(Boolean);
      colors.push(...productColors);
    }
    return [...new Set(colors)]; // dedupe
  }, [productDetail]);

  // Sizes
  const sizeList = useMemo(() => {
    if (!product?.attributes?.size) return [];
    return Array.isArray(product.attributes.size)
      ? product.attributes.size
      : String(product.attributes.size).split(",").map(s => s.trim()).filter(Boolean);
  }, [product]);

  // Cart state
  const existingCartItem = useMemo(() => {
    if (!product?.id || !selectedSize) return null;
    return cartItems.find(
      (item) =>
        item.id === product.id &&
        item.size === selectedSize &&
        item.color === selectedColor
    );
  }, [cartItems, product?.id, selectedSize, selectedColor]);

  const isInCart = !!existingCartItem;
  const quantity = isInCart ? existingCartItem.quantity : previewQuantity;

  // Set default size/color on mount
  useEffect(() => {
    if (colorList.length > 0 && !selectedColor) {
      setSelectedColor(colorList[0]);
    }
    if (sizeList.length > 0 && !selectedSize) {
      setSelectedSize(sizeList[0]);
    }
  }, [colorList, sizeList, selectedColor, selectedSize]);

  // Handlers
  const handleAddToCart = () => {
    if (!selectedSize) {
      alert("Please select a size before adding to cart.");
      return;
    }
    if (!product) return;

    if (existingCartItem) {
      if (quantity !== existingCartItem.quantity) {
        updateQuantity(existingCartItem.id, quantity);
      }
      return;
    }

    addToCart({
      id: product.id,
      title: product.title || "Product",
      price: parseFloat(product.price_info?.price || product.price_info?.originalPrice || 0),
      currency: product.price_info?.currencyCode || product.price_info?.currency || "USD",
      description: product.description || "",
      image: product.images?.[0] ? cleanImageUrl(product.images[0]) : "",
      size: selectedSize,
      color: selectedColor,
      variants: productDetail.variants || [],
      quantity: previewQuantity,
    });
  };

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) => (prev === 0 ? allImages.length - 1 : prev - 1));
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => (prev === allImages.length - 1 ? 0 : prev + 1));
  };

  const incrementQuantity = () => {
    if (isInCart) {
      updateQuantity(existingCartItem.id, existingCartItem.quantity + 1);
    } else {
      setPreviewQuantity((prev) => prev + 1);
    }
  };

  const decrementQuantity = () => {
    if (isInCart) {
      if (existingCartItem.quantity > 1) {
        updateQuantity(existingCartItem.id, existingCartItem.quantity - 1);
      } else {
        updateQuantity(existingCartItem.id, 0);
      }
    } else if (previewQuantity > 1) {
      setPreviewQuantity((prev) => prev - 1);
    }
  };

  // Derived data
  const originalPrice = parseFloat(product?.price_info?.originalPrice) || 0;
  const displayPrice = parseFloat(product?.price_info?.price) || originalPrice;
  const isOnSale = originalPrice > displayPrice && displayPrice > 0;
  const rating = parseFloat(product?.attributes?.rating) || 4.9;
  const reviewCount = parseInt(product?.attributes?.review_count) || 89;


  if (!product) {
    return (
      <div className="max-w-[1320px] mx-auto px-4 sm:px-6 py-8 ob-viewin">
        <button
          type="button"
          onClick={onBack}
          className="ob-btn ob-btn-ghost ob-btn-sm ob-backbtn"
        >
          <svg
            className="w-[14px] h-[14px] ob-backbtn-arrow"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.9}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m15 18-6-6 6-6" />
          </svg>
          Back
        </button>
        <div className="ob-empty">
          <div className="ob-empty-glyph" style={{ color: "var(--ob-err)" }}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.6}
              strokeLinecap="round"
              className="w-6 h-6"
            >
              <circle cx="12" cy="12" r="9" />
              <path d="M12 8v5M12 16.2v.1" />
            </svg>
          </div>
          <div className="ob-display text-[25px]" style={{ color: "var(--ob-ink)" }}>
            Product data missing
          </div>
        </div>
      </div>
    );
  }

  const currentImage = allImages[currentImageIndex] || allImages[0];
  const offPercent = isOnSale
    ? Math.round((1 - displayPrice / originalPrice) * 100)
    : 0;

  const specs = [
    { label: "SKU", value: product.id || "N/A" },
    {
      label: "Category",
      value: product.categories?.[0]?.split(" > ").pop() || "N/A",
    },
    { label: "Material", value: product.attributes?.fabric || "N/A" },
    { label: "Fit", value: product.attributes?.fit || "N/A" },
  ];

  return (
    <div className="max-w-[1320px] mx-auto px-4 sm:px-6 py-8 ob-viewin">
      <button
        type="button"
        onClick={onBack}
        className="ob-btn ob-btn-ghost ob-btn-sm ob-backbtn"
      >
        <svg
          className="w-[14px] h-[14px] ob-backbtn-arrow"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.9}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="m15 18-6-6 6-6" />
        </svg>
        Back
      </button>

      <div
        className={`ob-pd-grid grid gap-7 lg:gap-12 mt-3 ${
          tryOnOpen ? "ob-pd-staged" : "md:grid-cols-2"
        }`}
      >
        {/* ══ Gallery — or, while trying on, the mirror in its place ══ */}
        {tryOnOpen ? (
          <TryOnStage
            key={tryOnSession}
            productId={product.id}
            productTitle={product.title}
            image={currentImage ? cleanImageUrl(currentImage.url) : ""}
            count={allImages.length}
            index={currentImageIndex}
            onPrev={handlePrevImage}
            onNext={handleNextImage}
            autoStart={tryOnAutoStart}
            onExit={closeTryOn}
          />
        ) : (
        <div className="flex gap-3">
          {allImages.length > 1 && (
            <div className="ob-thumbrail ob-scroll">
              {allImages.map((image, index) => (
                <button
                  key={index}
                  type="button"
                  className={`ob-thumb ${currentImageIndex === index ? "ob-sel" : ""}`}
                  onClick={() => setCurrentImageIndex(index)}
                  aria-label={`View image ${index + 1}`}
                >
                  <img
                    src={cleanImageUrl(image.url)}
                    alt=""
                    loading="lazy"
                    onError={(e) => {
                      e.target.src = placeholderImage(`thumb${index}`, 100, 120);
                    }}
                  />
                </button>
              ))}
            </div>
          )}

          <div className="flex-1 min-w-0">
            <div className="ob-hero" onClick={() => openTryOn()}>
              <img
                src={currentImage ? cleanImageUrl(currentImage.url) : ""}
                alt={product.title}
                decoding="async"
                onError={(e) => {
                  e.target.src = placeholderImage(safeSeed(product.id), 800, 1066);
                }}
              />

              {allImages.length > 1 && (
                <>
                  <button
                    type="button"
                    className="ob-heronav ob-heronav-prev"
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePrevImage();
                    }}
                    aria-label="Previous image"
                  >
                    <ChevronLeftIcon sx={{ fontSize: 19 }} />
                  </button>
                  <button
                    type="button"
                    className="ob-heronav ob-heronav-next"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleNextImage();
                    }}
                    aria-label="Next image"
                  >
                    <ChevronRightIcon sx={{ fontSize: 19 }} />
                  </button>
                </>
              )}

              <button
                type="button"
                className="ob-tryonchip"
                onClick={(e) => {
                  e.stopPropagation();
                  openTryOn();
                }}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.75}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="w-[14px] h-[14px]"
                >
                  <rect x="5" y="2" width="14" height="17" rx="7" />
                  <path d="M9 22h6M12 19v3" />
                  <path d="M10 9a2 2 0 0 1 4 0" />
                </svg>
                Try it on
              </button>

              {allImages.length > 1 && (
                <div className="ob-herodots" onClick={(e) => e.stopPropagation()}>
                  {allImages.map((_, i) => (
                    <span
                      key={i}
                      className={`ob-herodot ${i === currentImageIndex ? "ob-on" : ""}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        )}

        {/* ══ Info ══ — beside the gallery normally; below the try-on stage,
            as story (left) and buying (right), in try-on mode ══ */}
        <div className="md:pt-1 ob-pd-info">
          <div className="ob-pd-intro">
          <span className="ob-chip ob-chip-accent">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.6}
              strokeLinecap="round"
              strokeLinejoin="round"
              className="w-[13px] h-[13px]"
            >
              <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9Z" />
            </svg>
            Orbiee found this for you
          </span>

          <h1 className="ob-display text-[clamp(1.75rem,4vw,2.4rem)] leading-[1.08] mt-3">
            {product.title}
          </h1>

          <div className="flex items-center gap-1.5 mt-2.5">
            <svg
              viewBox="0 0 24 24"
              fill="currentColor"
              className="w-3.5 h-3.5"
              style={{ color: "var(--ob-warn)" }}
            >
              <path d="M12 2.5l2.9 6 6.6.9-4.8 4.6 1.2 6.5L12 17.4 6.1 20.5l1.2-6.5L2.5 9.4l6.6-.9Z" />
            </svg>
            <span className="text-[12.5px] font-semibold ob-ink">{rating}</span>
            <span className="text-[12px] ob-ink3">({reviewCount} reviews)</span>
          </div>

          <div className="flex items-baseline gap-3 flex-wrap mt-4">
            <span
              className="text-[27px] font-bold ob-tabnum"
              style={{ color: isOnSale ? "var(--ob-err)" : "var(--ob-ink)" }}
            >
              ${displayPrice.toFixed(2)}
            </span>
            {isOnSale && (
              <>
                <span className="text-[15px] ob-ink3 line-through ob-tabnum">
                  ${originalPrice.toFixed(2)}
                </span>
                <span className="ob-chip ob-chip-err">{offPercent}% off</span>
              </>
            )}
          </div>

          {product.description && (
            <p className="text-[13.5px] ob-ink2 leading-relaxed mt-4">
              {product.description}
            </p>
          )}

          </div>

          <div className="ob-pd-buy">
          {/* Colour */}
          {colorList.length > 0 && (
            <div className="mt-6">
              <div className="flex items-baseline gap-2 mb-2">
                <span className="ob-lbl mb-0">Colour</span>
                <span className="text-[11.5px] ob-ink2 font-medium">
                  {selectedColor}
                </span>
              </div>
              <div className="flex gap-2 flex-wrap">
                {colorList.map((color, index) => (
                  <button
                    key={index}
                    type="button"
                    className={`ob-swatch ${selectedColor === color ? "ob-sel" : ""}`}
                    style={{ "--ob-swatch": getColorHex(color) }}
                    onClick={() => setSelectedColor(color)}
                    title={color}
                    aria-label={`Colour ${color}`}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Size */}
          {sizeList.length > 0 && (
            <div className="mt-6">
              <span className="ob-lbl">Size</span>
              <div className="flex gap-1.5 flex-wrap">
                {sizeList.map((size, index) => (
                  <button
                    key={index}
                    type="button"
                    className={`ob-size ${selectedSize === size ? "ob-sel" : ""}`}
                    onClick={() => setSelectedSize(size)}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Quantity */}
          <div className="mt-6">
            <span className="ob-lbl">Quantity</span>
            <span className="ob-qty">
              <button
                type="button"
                onClick={decrementQuantity}
                disabled={quantity <= 1}
                aria-label="Decrease quantity"
              >
                <RemoveIcon sx={{ fontSize: 15 }} />
              </button>
              <span>{quantity}</span>
              <button
                type="button"
                onClick={incrementQuantity}
                aria-label="Increase quantity"
              >
                <AddIcon sx={{ fontSize: 15 }} />
              </button>
            </span>
          </div>

          {/* Actions */}
          <div className="flex gap-2 mt-6">
            <button
              type="button"
              onClick={handleAddToCart}
              disabled={!selectedSize}
              className={`ob-btn ob-btn-lg flex-1 ${
                isInCart ? "ob-btn-ok" : "ob-btn-solid"
              }`}
            >
              {isInCart ? (
                <>
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2.4}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="w-[15px] h-[15px]"
                  >
                    <path d="m4 12.5 5 5L20 6.5" />
                  </svg>
                  In your bag
                </>
              ) : (
                <>
                  <ShoppingCartIcon sx={{ fontSize: 16 }} />
                  {selectedSize ? "Add to bag" : "Select a size"}
                </>
              )}
            </button>

            <div className="ob-actionslot">
              <WishlistToggle product={product} />
            </div>

            <button
              type="button"
              className="ob-btn ob-btn-ghost ob-btn-lg"
              aria-label="Share"
              title="Share"
            >
              <ShareIcon sx={{ fontSize: 16 }} />
            </button>
          </div>

          {/* Reassurance */}
          <div className="grid grid-cols-3 gap-2 mt-6 pt-5 ob-hair-t">
            {[
              { Icon: LocalShippingIcon, label: "Free shipping" },
              { Icon: SecurityIcon, label: "Secure payment" },
              { Icon: RefreshIcon, label: "Easy returns" },
            ].map(({ Icon, label }) => (
              <div key={label} className="text-center">
                <Icon sx={{ fontSize: 18, color: "var(--ob-ink-3)" }} />
                <div className="text-[10.5px] ob-ink2 font-medium mt-1">
                  {label}
                </div>
              </div>
            ))}
          </div>

          {/* Specs */}
          <div className="ob-panel mt-5 p-4">
            <div className="grid grid-cols-2 gap-x-4 gap-y-3">
              {specs.map(({ label, value }) => (
                <div key={label}>
                  <div className="ob-lbl mb-0.5">{label}</div>
                  <div className="text-[12.5px] font-medium ob-ink break-words">
                    {value}
                  </div>
                </div>
              ))}
            </div>
          </div>
          </div>
        </div>
      </div>

    </div>
  );
}
